import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine,
} from 'recharts';
import { useQuery } from '../hooks/useQuery';
import { api, SalesOverlayMonth, SalesOverlayReport } from '../lib/api';
import { fmtCurrency, fmtMonth } from '../lib/fmt';
import {
  SectionLabel, Card, CardHeader, Table, Tr, Td,
  SortableTh, ActionButton, Spinner, EmptyState,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';
import { downloadCsv } from '../lib/csv';

// ─── Sales overlay ──────────────────────────────────────────────────────────
// Four series, one chart:
//   - Shipped             — realised delivery value (attribution_month)
//   - Planned Shipped     — in-flight delivery value (due_date, unattributed)
//   - Closed Won          — HubSpot deals where is_closed_won=true (close_date)
//   - Forecast            — open deals weighted by stage probability (or the
//                           deal-level weighted_amount property when set)
//
// Past months show Shipped + Closed Won (actuals). Current month shows
// Planned Shipped + any closed-won so far. Future months show Forecast +
// Planned Shipped by due-date. The Revenue Recognized line overlays the
// bars showing worklog value done in each period (by work_date) — a past-
// month divergence between RevRec and Shipped flags worklogs piling up on
// unattributed deliverables. A reference line marks the current month.

// Colour assignments — kept close to existing CHART_COLORS semantics where
// they apply (green=shipped, amber=wip). Closed-won + forecast get blue +
// brand-yellow respectively so the four series are visually distinct in a
// dense bar chart.
// Two-tier palette:
//   - Saturated colours (top-level keys) — used for sparkline-tile values,
//     legend chips, chart LINE strokes, table cells, tooltips. These need
//     to read clearly against the page surface.
//   - Muted *_bar variants — ONLY used for Bar.fill in the chart. Lines
//     drawn on top need a calm background; saturated bar fills competed
//     with the line strokes and made the splines hard to follow.
//
// Both tiers use the colour-vars so they auto-invert in dark mode (e.g.
// --color-blue-bg flips to #0c2240) without a separate override.
// Hues spread across the colour wheel so the 10 series stay visually
// distinct. Cool → warm sweep, with each series at least ~30° from its
// neighbours where it matters:
//   green (Shipped/Planned)  ~120°
//   teal  (Paid)             ~180°  — was green-text, conflicted with Shipped
//   blue  (Closed Won)       ~210°
//   purple (Revenue Recognized) ~270°  — was brand-red, conflicted with Unpaid
//   coral (Active engagements) ~340°
//   red   (Unpaid)           ~0°
//   yellow (Forecast)        ~45°
//   amber  (Planned Shipped stroke) ~30°  — still in green family via stripe
//   gray  (Pipeline upside)  neutral
//   ink   (Active employees) neutral dark
const COLORS = {
  // Saturated (lines, chips, tile values, table accents)
  shipped:      'var(--color-green)',
  wip:          'var(--color-amber)',
  wip_done:     'var(--color-chart-purple)',
  closed_won:   'var(--color-blue)',
  forecast:     'var(--color-brand-yellow)',
  pipeline:     'var(--color-text-muted)',
  inv_paid:     'var(--color-chart-teal)',
  inv_unpaid:   'var(--color-red-text)',
  employees:    'var(--color-brand-ink)',
  engagements:  'var(--color-brand-coral)',
  // Engagements shipped sits on the right-axis count scale alongside
  // employees / engagements. Green ties it to the delivery domain
  // colour family on the dollar side so the operator reads the
  // relationship visually.
  engagements_shipped: 'var(--color-green)',
  // Deals closed won — count-side analogue of the blue Closed-Won area.
  // Same hue family so the operator reads "this is the count under the
  // closed-won curve" at a glance.
  deals_closed_won: 'var(--color-blue)',
  // Open engagements — count line, amber (ties to Planned Shipped /
  // "not yet shipped" on the delivery side).
  open_engagements: 'var(--color-amber)',
  // Average open-engagement age (weeks) — purple, count axis. Distinct from
  // the other count lines so the aging trend reads on its own.
  open_age: 'var(--color-chart-purple)',

  // Muted bar fills — pale tints of the matching saturated colour above.
  // Same family, lower visual weight so spline lines on top read clearly.
  shipped_bar:    'var(--color-green-bg)',
  wip_bar:        'var(--color-amber-bg)',
  closed_won_bar: 'var(--color-blue-bg)',
  forecast_bar:   'var(--color-amber-bg)',
  pipeline_bar:   'var(--color-border-strong)',
  inv_paid_bar:   'var(--color-green-bg)',
  inv_unpaid_bar: 'var(--color-red-bg)',

  // Chrome
  grid:         'var(--color-border)',
  axis:         'var(--color-text-muted)',
  refLine:      'var(--color-text-hint)',
  // Stroke between stacked segments — surface var so the divider blends
  // into the page on light theme and into the card on dark theme.
  segmentEdge:  'var(--color-surface)',
} as const;

interface ChartDatum {
  month: string;          // "MMM 'YY" short label for the axis
  monthKey: string;       // YYYY-MM for sorting / tooltip
  Shipped: number;
  /** Was "WIP planned" — in-flight value bucketed by deliverable.due_date.
   *  Display name kept as "Planned Shipped" so it reads consistently with
   *  Shipped as the realised counterpart. */
  'Planned Shipped': number;
  'Closed Won': number;
  Forecast: number;
  /** Was "WIP done" — value of worklog actually done in the period
   *  (work_date buckets). Surfaced as "Revenue Recognized" so the term
   *  matches the finance reading (work landed = revenue recognized). */
  'Revenue Recognized': number;
  /** Top segment of the sales stack — gross unweighted pipeline MINUS the
   *  weighted Forecast. Stacked on top of Forecast so total stack height
   *  equals Closed Won + Pipeline_unweighted (the "max possible booking
   *  value" for the month if every open deal closes at full amount). */
  'Pipeline upside': number;
  Paid: number;
  Unpaid: number;         // = max(0, Billed - Paid). Stack height = Billed.
  'Active employees': number;
  'Active engagements': number;
  'Engagements shipped': number;
  'Deals closed won': number;
  'Open engagements': number;
  'Avg open age (wks)': number;
  // Context-only fields — rendered in the tooltip but not as their own bars.
  _delivery_total: number;  // Shipped + Planned Shipped; surfaced in tooltip.
  _billed_total: number;    // Paid + Unpaid; surfaced in tooltip as "Billed".
  _pipeline_unweighted: number;
  _isCurrent: boolean;
  _isFuture: boolean;
}

/** Default chart range: current month ±12. Computed client-side so the
 *  From/To pickers have values immediately and the first fetch matches the
 *  backend's own default window. */
function defaultRange(): { from: string; to: string } {
  const now = new Date();
  const ms = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return {
    from: ms(new Date(now.getFullYear(), now.getMonth() - 12, 1)),
    to:   ms(new Date(now.getFullYear(), now.getMonth() + 12, 1)),
  };
}

export default function AdminSalesOverlayPage() {
  // Date range — scopes the chart, the breakdown table, AND the rollup cards.
  // Changing either bound refetches the report (backend-filtered).
  const [{ from: fromMonth, to: toMonth }, setRange] = useState(defaultRange);
  // Engagement-type filter — empty = all types. Scopes delivery + engagement
  // metrics only (the HubSpot $ series + invoicing aren't engagement-linked).
  const [engagementTypes, setEngagementTypes] = useState<string[]>([]);
  const { data, isLoading, isFetching, error } =
    useQuery(
      () => api.admin.salesOverlay(fromMonth, toMonth, engagementTypes),
      [fromMonth, toMonth, engagementTypes],
    );

  // Per-series visibility — one toggle per metric (not per domain) so the
  // operator can isolate any combination, e.g. just "Shipped" + "Closed Won"
  // or just "Deals closed won" + "Engagements shipped". Counts start hidden
  // (right-axis scale competes with the dollar series).
  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(SERIES.map((s) => [s.name, s.domain !== 'counts'])),
  );
  const vis = (name: string) => visible[name] ?? false;
  const toggleOne = (name: string) => setVisible((v) => ({ ...v, [name]: !v[name] }));
  const toggleGroup = (dom: Domain, on: boolean) =>
    setVisible((v) => {
      const next = { ...v };
      for (const s of SERIES) if (s.domain === dom) next[s.name] = on;
      return next;
    });

  // Build chart series before any early returns so the hook order stays stable.
  // The backend already returns exactly the requested range, so no client-side
  // windowing — chartData maps the payload 1:1.
  const chartData: ChartDatum[] = useMemo(() => {
    if (!data) return [];
    return data.months.map((m: SalesOverlayMonth) => ({
      month: shortMonthLabel(m.month),
      monthKey: m.month,
      Shipped:              Math.round(m.shipped),
      'Planned Shipped':    Math.round(m.wip),
      'Closed Won':         Math.round(m.closed_won),
      Forecast:             Math.round(m.forecast),
      'Revenue Recognized': Math.round(m.wip_done),
      // Pipeline upside = gross unweighted pipeline minus weighted Forecast.
      // Forecast ⊂ Pipeline_unweighted (Forecast is the probability-weighted
      // subset), so stacking gross on top of Forecast would double-count.
      // The differential gives a mutually-exclusive segment whose top edge
      // marks the full unweighted pipeline ceiling.
      'Pipeline upside':    Math.max(0, Math.round(m.forecast_unweighted - m.forecast)),
      Paid:              Math.round(m.invoice_paid),
      // Paid ⊂ Billed, so stacking the gross Billed value on top of Paid
      // double-counts. Stack Paid + (Billed - Paid) instead — the total
      // height equals Billed, and the two segments are mutually exclusive.
      Unpaid:            Math.max(0, Math.round(m.invoice_billed - m.invoice_paid)),
      'Active employees':    m.active_employees,
      'Active engagements':  m.active_engagements,
      'Engagements shipped': m.engagements_shipped,
      'Deals closed won':    m.deals_closed_won,
      'Open engagements':    m.open_engagements,
      'Avg open age (wks)':  m.avg_open_age_weeks,
      _delivery_total:      Math.round(m.shipped + m.wip),
      _billed_total:        Math.round(m.invoice_billed),
      _pipeline_unweighted: Math.round(m.forecast_unweighted),
      _isCurrent:        m.month === data.current_month,
      _isFuture:         m.month > data.current_month,
    }));
  }, [data]);

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return <EmptyState message="No data" />;

  const t = data.totals;
  const currentMonthDatum = chartData.find((d) => d._isCurrent);
  // Index of the current month in the series, so each sparkline tile can mark
  // "now". -1 (current month outside the selected range) hides the marker.
  const nowIdx = chartData.findIndex((d) => d._isCurrent);

  // Quick-set the range to current month ±n. Complements the explicit pickers.
  const applyPreset = (n: number) => {
    const now = new Date();
    const ms = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    setRange({
      from: ms(new Date(now.getFullYear(), now.getMonth() - n, 1)),
      to:   ms(new Date(now.getFullYear(), now.getMonth() + n, 1)),
    });
  };
  const presetActive = (n: number) => {
    const now = new Date();
    const ms = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return fromMonth === ms(new Date(now.getFullYear(), now.getMonth() - n, 1))
        && toMonth   === ms(new Date(now.getFullYear(), now.getMonth() + n, 1));
  };

  return (
    <>
      <SectionLabel>Sales overlay</SectionLabel>

      {/* Range caption — every tile + total below is scoped to this window. */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        margin: '8px 4px -2px', fontSize: 10, color: 'var(--color-text-hint)',
        textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        <span>Range · {fmtMonth(data.from_month)} → {fmtMonth(data.to_month)}{isFetching ? ' · updating…' : ''}</span>
        <span>now: {fmtMonth(data.current_month)}</span>
      </div>

      {/* Dense metric strip — each tile is scoped to the selected range:
          dollar tiles sum the range, count tiles are distinct over the range.
          Layout breaks to 3-up at narrower viewports via minmax(180px, 1fr). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
        <SparklineTile
          label="Closed Won"
          value={fmtCurrency(t.closed_won)}
          sub="in range"
          color={COLORS.closed_won}
          series={chartData.map((d) => d['Closed Won'])}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Shipped"
          value={fmtCurrency(t.shipped)}
          sub="in range"
          color={COLORS.shipped}
          series={chartData.map((d) => d.Shipped)}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Planned Shipped"
          value={fmtCurrency(t.wip)}
          sub="in range"
          color={COLORS.wip}
          series={chartData.map((d) => d['Planned Shipped'])}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Forecast (weighted)"
          value={fmtCurrency(t.forecast)}
          sub={`${fmtCurrency(t.forecast_unweighted)} unweighted`}
          color={COLORS.forecast}
          series={chartData.map((d) => d.Forecast)}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Billed"
          value={fmtCurrency(t.billed)}
          sub={`${fmtCurrency(t.paid)} paid`}
          color={COLORS.inv_paid}
          series={chartData.map((d) => d._billed_total)}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Active employees"
          value={String(t.active_employees)}
          sub="distinct in range"
          color={COLORS.employees}
          series={chartData.map((d) => d['Active employees'])}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Active engagements"
          value={String(t.active_engagements)}
          sub="distinct in range"
          color={COLORS.engagements}
          series={chartData.map((d) => d['Active engagements'])}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Engagements shipped"
          value={String(t.engagements_shipped)}
          sub="distinct in range"
          color={COLORS.engagements_shipped}
          series={chartData.map((d) => d['Engagements shipped'])}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Open engagements"
          value={String(t.open_engagements)}
          sub="distinct open in range"
          color={COLORS.open_engagements}
          series={chartData.map((d) => d['Open engagements'])}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Deals closed won"
          value={String(t.deals_closed_won)}
          sub="distinct in range"
          color={COLORS.deals_closed_won}
          series={chartData.map((d) => d['Deals closed won'])}
          nowIdx={nowIdx}
        />
        <SparklineTile
          label="Avg open age"
          value={`${t.avg_open_age_weeks} wks`}
          sub="open engagements · end of range"
          color={COLORS.open_age}
          series={chartData.map((d) => d['Avg open age (wks)'])}
          nowIdx={nowIdx}
        />
      </div>

      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Monthly overlay</span>
          {/* Date-range controls — explicit From/To month pickers plus
              quick ±N presets centred on the current month. */}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <span style={{
              fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.6,
              color: 'var(--color-text-hint)', fontWeight: 500,
            }}>
              Range
            </span>
            <MonthInput value={fromMonth} onChange={(v) => setRange((r) => ({ ...r, from: v }))} />
            <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>→</span>
            <MonthInput value={toMonth} onChange={(v) => setRange((r) => ({ ...r, to: v }))} />
            <span style={{ width: 1, height: 18, background: 'var(--color-border)', margin: '0 4px' }} />
            <PeriodChip active={presetActive(3)}  onSelect={() => applyPreset(3)}  label="±3M" />
            <PeriodChip active={presetActive(6)}  onSelect={() => applyPreset(6)}  label="±6M" />
            <PeriodChip active={presetActive(12)} onSelect={() => applyPreset(12)} label="±12M" />
          </span>
        </CardHeader>

        {/* Engagement-type filter — scopes the delivery + engagement-count
            metrics (NOT the HubSpot $ series or invoicing, which aren't
            engagement-linked). "All" = no filter. Only rendered when the sync
            has surfaced at least one engagement type. */}
        {data.available_engagement_types.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
            padding: '8px 16px 4px',
          }}>
            <span style={{
              fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.6,
              color: 'var(--color-text-hint)', fontWeight: 500,
            }}>
              Engagement type
            </span>
            <MultiSelectDropdown
              groups={data.available_engagement_types}
              selected={engagementTypes}
              onChange={setEngagementTypes}
              allLabel="All types"
            />
            <span style={{ fontSize: 9, color: 'var(--color-text-hint)' }}>
              scopes delivery + engagement counts (not HubSpot $ / invoicing)
            </span>
          </div>
        )}

        {/* Per-series toggles — grouped by domain. The small-caps domain
            label toggles the whole group; each chip toggles one series and
            doubles as the legend (colour swatch + on/off state). */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '6px 18px',
          padding: '4px 16px 10px', borderBottom: '0.5px solid var(--color-border)',
        }}>
          {DOMAIN_ORDER.map((dom) => {
            const series = SERIES.filter((s) => s.domain === dom);
            const allOn = series.every((s) => vis(s.name));
            return (
              <span key={dom} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => toggleGroup(dom, !allOn)}
                  title={allOn ? `Hide all ${DOMAIN_LABEL[dom]}` : `Show all ${DOMAIN_LABEL[dom]}`}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 500,
                    color: allOn ? 'var(--color-text-muted)' : 'var(--color-text-hint)',
                  }}
                >
                  {DOMAIN_LABEL[dom]}
                </button>
                {series.map((s) => (
                  <SeriesToggle
                    key={s.name}
                    label={s.name}
                    color={s.color}
                    shape={s.shape}
                    pattern={s.pattern}
                    active={vis(s.name)}
                    onToggle={() => toggleOne(s.name)}
                  />
                ))}
              </span>
            );
          })}
        </div>

        <div style={{ width: '100%', height: 460, padding: '8px 16px 16px' }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 4 }}>
              {/* SVG pattern fills tie related areas visually:
                    - Delivery family   — green hue, solid for Shipped /
                                          zebra stripes for Planned Shipped.
                    - Sales family      — diagonal stripes across all three,
                                          density indicating certainty
                                          (Closed Won solid, Forecast medium,
                                          Pipeline sparse).
                  Patterns use CSS var strokes so they auto-flip in dark mode. */}
              <defs>
                <pattern id="stripe-green" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="7" style={{ stroke: 'var(--color-green)' }} strokeWidth={2.5} />
                </pattern>
                <pattern id="stripe-blue" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="6" style={{ stroke: 'var(--color-blue)' }} strokeWidth={1.5} />
                </pattern>
                <pattern id="stripe-yellow" patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="7" style={{ stroke: 'var(--color-brand-yellow)' }} strokeWidth={2} />
                </pattern>
                <pattern id="stripe-gray" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="10" style={{ stroke: 'var(--color-text-muted)' }} strokeWidth={1} />
                </pattern>
              </defs>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fill: COLORS.axis }}
                stroke={COLORS.grid}
                interval={0}
                angle={-30}
                textAnchor="end"
                height={48}
              />
              <YAxis
                yAxisId="value"
                tick={{ fontSize: 10, fill: COLORS.axis }}
                stroke={COLORS.grid}
                tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`}
              />
              {/* Right axis carries the distinct-count lines. Different scale
                  (units, not dollars) so it needs its own axis; clamp at 0
                  since counts never go negative. */}
              <YAxis
                yAxisId="count"
                orientation="right"
                tick={{ fontSize: 10, fill: COLORS.axis }}
                stroke={COLORS.grid}
                allowDecimals={false}
                domain={[0, 'auto']}
                tickFormatter={(v) => `${v}`}
              />
              <Tooltip content={<OverlayTooltip />} />
              {/* Mark "today" so the past/future divide is obvious. yAxisId is
                  REQUIRED when the chart has multiple Y-axes — Recharts throws
                  an invariant during layout otherwise. */}
              {currentMonthDatum && (
                <ReferenceLine
                  yAxisId="value"
                  x={currentMonthDatum.month}
                  stroke={COLORS.refLine}
                  strokeDasharray="3 3"
                  label={{ value: 'now', position: 'top', fontSize: 9, fill: COLORS.refLine }}
                />
              )}
              {/* Render order matters in Recharts — children rendered LATER
                  appear ON TOP. Areas first (= behind) so the lines layer
                  cleanly. Each series gated on its own visibility toggle. */}

              {vis('Shipped') && (
                <Area
                  yAxisId="value" type="monotone" dataKey="Shipped" stackId="delivery"
                  fill={COLORS.shipped} fillOpacity={0.3} stroke={COLORS.shipped}
                  strokeWidth={1.5} isAnimationActive={false}
                />
              )}
              {vis('Planned Shipped') && (
                <Area
                  yAxisId="value" type="monotone" dataKey="Planned Shipped" stackId="delivery"
                  fill="url(#stripe-green)" fillOpacity={0.7} stroke={COLORS.shipped}
                  strokeWidth={1.5} isAnimationActive={false}
                />
              )}

              {/* Sales domain — Closed Won + Forecast on one stackId,
                  Pipeline upside on the same stack so it sits on top of
                  Forecast. Each independently togglable. */}
              {vis('Closed Won') && (
                <Area
                  yAxisId="value" type="monotone" dataKey="Closed Won" stackId="sales"
                  fill={COLORS.closed_won} fillOpacity={0.28} stroke={COLORS.closed_won}
                  strokeWidth={1.5} isAnimationActive={false}
                />
              )}
              {vis('Forecast') && (
                <Area
                  yAxisId="value" type="monotone" dataKey="Forecast" stackId="sales"
                  fill="url(#stripe-yellow)" fillOpacity={0.6} stroke={COLORS.forecast}
                  strokeWidth={1.5} isAnimationActive={false}
                />
              )}
              {vis('Pipeline upside') && (
                <Area
                  yAxisId="value" type="monotone" dataKey="Pipeline upside" stackId="sales"
                  fill="url(#stripe-gray)" fillOpacity={0.6} stroke={COLORS.pipeline}
                  strokeWidth={1.5} isAnimationActive={false}
                />
              )}

              {/* Invoicing domain — Paid + Unpaid stacked area. */}
              {vis('Paid') && (
                <Area
                  yAxisId="value" type="monotone" dataKey="Paid" stackId="invoicing"
                  fill={COLORS.inv_paid} fillOpacity={0.18} stroke={COLORS.inv_paid}
                  strokeWidth={1.5} isAnimationActive={false}
                />
              )}
              {vis('Unpaid') && (
                <Area
                  yAxisId="value" type="monotone" dataKey="Unpaid" stackId="invoicing"
                  fill={COLORS.inv_unpaid} fillOpacity={0.18} stroke={COLORS.inv_unpaid}
                  strokeWidth={1.5} isAnimationActive={false}
                />
              )}

              {/* Revenue Recognized — line overlaying the delivery area. */}
              {vis('Revenue Recognized') && (
                <Line
                  yAxisId="value" type="monotone" dataKey="Revenue Recognized"
                  stroke={COLORS.wip_done} strokeWidth={2}
                  dot={{ r: 2, fill: COLORS.wip_done }} activeDot={{ r: 4 }} isAnimationActive={false}
                />
              )}

              {/* Right-axis count lines — each on the shared count scale, with
                  distinct stroke dashes so they read apart at equal values. */}
              {vis('Active employees') && (
                <Line
                  yAxisId="count" type="monotone" dataKey="Active employees"
                  stroke={COLORS.employees} strokeWidth={2} strokeDasharray="4 2"
                  dot={{ r: 2, fill: COLORS.employees }} activeDot={{ r: 4 }} isAnimationActive={false}
                />
              )}
              {vis('Active engagements') && (
                <Line
                  yAxisId="count" type="monotone" dataKey="Active engagements"
                  stroke={COLORS.engagements} strokeWidth={2} strokeDasharray="2 3"
                  dot={{ r: 2, fill: COLORS.engagements }} activeDot={{ r: 4 }} isAnimationActive={false}
                />
              )}
              {vis('Engagements shipped') && (
                <Line
                  yAxisId="count" type="monotone" dataKey="Engagements shipped"
                  stroke={COLORS.engagements_shipped} strokeWidth={2} strokeDasharray="6 2"
                  dot={{ r: 2, fill: COLORS.engagements_shipped }} activeDot={{ r: 4 }} isAnimationActive={false}
                />
              )}
              {vis('Deals closed won') && (
                <Line
                  yAxisId="count" type="monotone" dataKey="Deals closed won"
                  stroke={COLORS.deals_closed_won} strokeWidth={2} strokeDasharray="6 2 2 2"
                  dot={{ r: 2, fill: COLORS.deals_closed_won }} activeDot={{ r: 4 }} isAnimationActive={false}
                />
              )}
              {vis('Open engagements') && (
                <Line
                  yAxisId="count" type="monotone" dataKey="Open engagements"
                  stroke={COLORS.open_engagements} strokeWidth={2} strokeDasharray="3 3"
                  dot={{ r: 2, fill: COLORS.open_engagements }} activeDot={{ r: 4 }} isAnimationActive={false}
                />
              )}
              {vis('Avg open age (wks)') && (
                <Line
                  yAxisId="count" type="monotone" dataKey="Avg open age (wks)"
                  stroke={COLORS.open_age} strokeWidth={2} strokeDasharray="1 2"
                  dot={{ r: 2, fill: COLORS.open_age }} activeDot={{ r: 4 }} isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Monthly breakdown</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            All values in USD. Forecast column shows weighted; unweighted in parentheses.
          </span>
          <span style={{ marginLeft: 'auto' }}>
            <ActionButton
              onClick={() => downloadOverlayCsv(data.months)}
              variant="default"
              disabled={data.months.length === 0}
            >
              Export CSV
            </ActionButton>
          </span>
        </CardHeader>
        <BreakdownTable months={data.months} currentMonth={data.current_month} />
      </Card>
    </>
  );
}

/** Compact metric tile with a small line sparkline. Replaces the legacy
 *  MetricCard on this page — narrower, with trend context built in. The
 *  sparkline shares its colour with the matching series in the main chart
 *  so the operator can visually link a tile to the chart series. */
function SparklineTile({
  label, value, sub, color, series, nowIdx,
}: {
  label: string;
  value: string;
  sub?: string;
  color: string;
  /** Optional time-series for the mini sparkline. Omit for snapshot-only
   *  metrics (e.g. open engagements — we don't carry historical data
   *  for that state so a sparkline would be a misleading flat line). */
  series?: number[];
  /** Index of the "current month" datum in `series`, so the sparkline can
   *  draw a vertical marker showing where in the timeline `value` sits.
   *  Undefined or out-of-range disables the marker. */
  nowIdx?: number;
}) {
  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '0.5px solid var(--color-border)',
      borderRadius: 'var(--radius-sm)',
      padding: '8px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
    }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{sub}</div>
      )}
      {series && <MiniSparkline series={series} color={color} nowIdx={nowIdx} />}
    </div>
  );
}

/** Pure SVG sparkline. No Recharts dependency on the tiles so they stay
 *  cheap to render in a row of 6+. Auto-scales to series max; flat zero
 *  series renders as a flat baseline rather than a divide-by-zero crash.
 *
 *  `nowIdx` draws a faint vertical line at the corresponding position so
 *  the operator can see WHERE in the timeline the headline value sits.
 *  Without the marker a tile feels rootless — the headline number could
 *  be from anywhere in the 25-month series. */
function MiniSparkline({ series, color, nowIdx }: { series: number[]; color: string; nowIdx?: number }) {
  const width = 100;
  const height = 22;
  const max = Math.max(...series, 1);
  const stepX = series.length > 1 ? width / (series.length - 1) : width;
  // Map series → polyline points. NaN-safe (treat non-finite values as 0).
  const points = series.map((v, i) => {
    const x = i * stepX;
    const y = height - (Number.isFinite(v) ? (v / max) : 0) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const nowX = nowIdx != null && nowIdx >= 0 && nowIdx < series.length
    ? nowIdx * stepX
    : null;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ marginTop: 2 }}>
      {/* Now marker — drawn behind the line so the polyline stroke wins
          where they cross. Dashed so it reads as a context guide, not data. */}
      {nowX != null && (
        <line
          x1={nowX} y1={0} x2={nowX} y2={height}
          style={{ stroke: 'var(--color-text-hint)' }}
          strokeWidth={1}
          strokeDasharray="2 2"
          opacity={0.6}
        />
      )}
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={0.85}
      />
      {/* Filled dot at the "now" data point so the headline value's
          position on the trend is obvious at a glance. */}
      {nowX != null && (() => {
        const v = series[nowIdx!];
        const y = height - (Number.isFinite(v) ? (v / max) : 0) * (height - 2) - 1;
        return <circle cx={nowX} cy={y} r={2.2} fill={color} />;
      })()}
    </svg>
  );
}

/** Segmented-control chip for the time-window selector. Same visual
 *  language as ToggleChip but reads as a single-choice radio group — only
 *  one PeriodChip should be active at a time within a group. */
function PeriodChip({ active, onSelect, label }: { active: boolean; onSelect: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        background: active ? 'var(--color-blue-bg)' : 'var(--color-surface)',
        border: '0.5px solid var(--color-border-strong)',
        borderRadius: 'var(--radius-sm)',
        padding: '2px 8px',
        fontSize: 10,
        cursor: 'pointer',
        color: active ? 'var(--color-blue-text)' : 'var(--color-text-muted)',
        fontWeight: 500,
        minWidth: 32,
      }}
    >
      {label}
    </button>
  );
}

/** Compact grouped multi-select dropdown: a chip-styled button showing the
 *  selection summary, opening a popover that nests sub-items under their
 *  cascading parent group. A group-level checkbox selects/clears all its
 *  sub-items; a group whose single item equals its name renders as one leaf.
 *  Selection is a flat list of leaf values. Empty = "all" (no filter).
 *  Closes on outside click. */
function MultiSelectDropdown({
  groups, selected, onChange, allLabel,
}: {
  groups: Array<{ group: string; items: string[] }>;
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const label = selected.length === 0
    ? allLabel
    : selected.length === 1 ? selected[0] : `${selected.length} selected`;
  const toggleLeaf = (opt: string) =>
    onChange(selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt]);
  const toggleGroup = (items: string[]) => {
    const allOn = items.every((i) => selected.includes(i));
    onChange(allOn
      ? selected.filter((s) => !items.includes(s))
      : Array.from(new Set([...selected, ...items])));
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: selected.length ? 'var(--color-blue-bg)' : 'var(--color-surface)',
          border: '0.5px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-sm)',
          padding: '2px 8px',
          fontSize: 10,
          cursor: 'pointer',
          color: selected.length ? 'var(--color-blue-text)' : 'var(--color-text-muted)',
          fontWeight: 500,
          display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 220,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: 8 }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 30, marginTop: 4, minWidth: 200, maxHeight: 320, overflow: 'auto',
          background: 'var(--color-surface)', border: '0.5px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: 4,
        }}>
          <button
            type="button"
            onClick={() => onChange([])}
            style={{
              display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
              padding: '4px 8px', fontSize: 11, cursor: 'pointer',
              color: selected.length === 0 ? 'var(--color-blue-text)' : 'var(--color-text-muted)',
              fontWeight: selected.length === 0 ? 600 : 400,
            }}
          >
            {allLabel}
          </button>
          <div style={{ height: 1, background: 'var(--color-border)', margin: '2px 0' }} />
          {groups.map(({ group, items }) => {
            // Parent-only option (no sub-items): render as a single leaf.
            if (items.length === 1 && items[0] === group) {
              return (
                <label key={group} style={leafRowStyle}>
                  <input type="checkbox" checked={selected.includes(group)} onChange={() => toggleLeaf(group)} />
                  <span style={ellipsisStyle}>{group}</span>
                </label>
              );
            }
            const allOn = items.every((i) => selected.includes(i));
            return (
              <div key={group} style={{ marginTop: 2 }}>
                <label style={{ ...leafRowStyle, fontWeight: 600 }}>
                  <input type="checkbox" checked={allOn} onChange={() => toggleGroup(items)} />
                  <span style={ellipsisStyle}>{group}</span>
                </label>
                {items.map((it) => (
                  <label key={it} style={{ ...leafRowStyle, paddingLeft: 22 }}>
                    <input type="checkbox" checked={selected.includes(it)} onChange={() => toggleLeaf(it)} />
                    <span style={ellipsisStyle}>{it === group ? `${it} (no sub-type)` : it}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const leafRowStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px',
  fontSize: 11, cursor: 'pointer', color: 'var(--color-text)',
};
const ellipsisStyle: CSSProperties = {
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

/** Native month picker styled for the dense control bar. Value is the
 *  YYYY-MM string the API expects; empty changes (picker cleared) are ignored
 *  so we never send a blank bound. */
function MonthInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="month"
      value={value}
      onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
      style={{
        fontSize: 10,
        padding: '2px 6px',
        border: '0.5px solid var(--color-border-strong)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--color-surface)',
        color: 'var(--color-text)',
        fontVariantNumeric: 'tabular-nums',
      }}
    />
  );
}

/** Combined legend + visibility toggle. Shows the series colour swatch
 *  (mirroring the chart fill: solid / striped / line) plus the label; click
 *  toggles the series. Dimmed when off so the row also reads as a legend. */
function SeriesToggle({
  label, color, active, onToggle, shape = 'square', pattern,
}: {
  label: string;
  color: string;
  active: boolean;
  onToggle: () => void;
  shape?: 'square' | 'line';
  pattern?: 'stripe' | 'sparse';
}) {
  let background: string = color;
  if (pattern === 'stripe') background = `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 5px)`;
  else if (pattern === 'sparse') background = `repeating-linear-gradient(45deg, ${color} 0 1px, transparent 1px 6px)`;
  const swatch = shape === 'line'
    ? <span style={{ width: 12, height: 2, background: color, borderRadius: 1 }} />
    : <span style={{ width: 10, height: 10, borderRadius: 2, background, border: '0.5px solid var(--color-border-strong)' }} />;
  return (
    <button
      type="button"
      onClick={onToggle}
      title={active ? `Hide ${label}` : `Show ${label}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: active ? 'var(--color-surface)' : 'transparent',
        border: '0.5px solid var(--color-border-strong)',
        borderRadius: 'var(--radius-sm)',
        padding: '2px 8px',
        fontSize: 10,
        cursor: 'pointer',
        color: active ? 'var(--color-text)' : 'var(--color-text-hint)',
        opacity: active ? 1 : 0.55,
      }}
    >
      {swatch}
      {label}
    </button>
  );
}

interface TooltipEntry { name?: string; value?: number | string; color?: string; payload?: ChartDatum }

// Map each series name to a logical domain so the tooltip groups them
// the same way the legend below the chart does. Anything not in the
// map renders ungrouped (defensive, shouldn't happen with the current
// series set).
type Domain = 'delivery' | 'sales' | 'invoicing' | 'counts';

/** Series catalog — single source of truth for the per-series toggle controls
 *  (which double as the legend) and the tooltip grouping. `shape`/`pattern`
 *  mirror how each series renders in the chart so the toggle swatch reads as
 *  the same visual. Order within a domain = render/stack order. */
const SERIES: Array<{
  name: string;
  domain: Domain;
  color: string;
  shape?: 'square' | 'line';
  pattern?: 'stripe' | 'sparse';
}> = [
  { name: 'Shipped',             domain: 'delivery',  color: COLORS.shipped },
  { name: 'Planned Shipped',     domain: 'delivery',  color: COLORS.shipped, pattern: 'stripe' },
  { name: 'Revenue Recognized',  domain: 'delivery',  color: COLORS.wip_done, shape: 'line' },
  { name: 'Closed Won',          domain: 'sales',     color: COLORS.closed_won },
  { name: 'Forecast',            domain: 'sales',     color: COLORS.forecast, pattern: 'stripe' },
  { name: 'Pipeline upside',     domain: 'sales',     color: COLORS.pipeline, pattern: 'sparse' },
  { name: 'Paid',                domain: 'invoicing', color: COLORS.inv_paid },
  { name: 'Unpaid',              domain: 'invoicing', color: COLORS.inv_unpaid },
  { name: 'Active employees',    domain: 'counts',    color: COLORS.employees,           shape: 'line' },
  { name: 'Active engagements',  domain: 'counts',    color: COLORS.engagements,         shape: 'line' },
  { name: 'Engagements shipped', domain: 'counts',    color: COLORS.engagements_shipped, shape: 'line' },
  { name: 'Deals closed won',    domain: 'counts',    color: COLORS.deals_closed_won,    shape: 'line' },
  { name: 'Open engagements',    domain: 'counts',    color: COLORS.open_engagements,    shape: 'line' },
  { name: 'Avg open age (wks)',  domain: 'counts',    color: COLORS.open_age,            shape: 'line' },
];

// Series → domain, derived from the catalog. Used by the tooltip to group
// rows the same way the toggle controls do.
const SERIES_DOMAIN: Record<string, Domain> =
  Object.fromEntries(SERIES.map((s) => [s.name, s.domain]));

const DOMAIN_LABEL: Record<Domain, string> = {
  delivery:  'Delivery',
  sales:     'Sales pipeline',
  invoicing: 'Invoicing',
  counts:    'Distinct counts',
};
const DOMAIN_ORDER: Domain[] = ['delivery', 'sales', 'invoicing', 'counts'];

function OverlayTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipEntry[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null;
  // All payload entries share the same datum (Recharts feeds one row
  // per tooltip). Used for the derived domain-summary rows.
  const datum = payload[0]?.payload;

  // Bucket entries by domain so we can render a small caps header per
  // group — mirrors the legend grouping below the chart. Within each
  // group we preserve the chart's child order (which is also the
  // visual stacking order).
  const byDomain: Record<string, TooltipEntry[]> = { delivery: [], sales: [], invoicing: [], counts: [] };
  for (const p of payload) {
    const dom = (p.name && SERIES_DOMAIN[p.name]) ?? 'delivery';
    byDomain[dom].push(p);
  }

  const isCount = (name?: string) =>
    name === 'Active employees' || name === 'Active engagements'
    || name === 'Engagements shipped' || name === 'Deals closed won'
    || name === 'Open engagements' || name === 'Avg open age (wks)';
  const fmt = (p: TooltipEntry) =>
    typeof p.value === 'number'
      ? (isCount(p.name) ? `${p.value}` : fmtCurrency(p.value))
      : String(p.value ?? '');

  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '0.5px solid var(--color-border-strong)',
      borderRadius: 'var(--radius-sm)',
      padding: '8px 12px',
      fontSize: 11,
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      minWidth: 220,
    }}>
      <div style={{ fontWeight: 500, marginBottom: 6 }}>{label}</div>
      {DOMAIN_ORDER.map((dom, i) => {
        const entries = byDomain[dom];
        // Domain-level derived totals (Delivery sum, Billed sum, gross
        // Pipeline) — render alongside the series rows under the right
        // domain header so the math is visible where it belongs.
        const summary: { label: string; value: number } | null = (() => {
          if (!datum) return null;
          if (dom === 'delivery'  && datum._delivery_total > 0)       return { label: 'Delivery (Shipped + Planned)', value: datum._delivery_total };
          if (dom === 'invoicing' && datum._billed_total > 0)         return { label: 'Billed (Paid + Unpaid)',        value: datum._billed_total };
          if (dom === 'sales'     && datum._pipeline_unweighted > 0)  return { label: 'Pipeline (gross unweighted)',   value: datum._pipeline_unweighted };
          return null;
        })();
        if (entries.length === 0 && !summary) return null;
        return (
          <div key={dom} style={{ marginTop: i === 0 ? 0 : 6, display: 'grid', gap: 2 }}>
            <div style={{
              fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.6,
              color: 'var(--color-text-hint)', fontWeight: 500, marginBottom: 1,
            }}>
              {DOMAIN_LABEL[dom]}
            </div>
            {entries.map((p, j) => (
              <div key={j} style={{ display: 'flex', gap: 8 }}>
                <span style={{ width: 8, height: 8, background: p.color, borderRadius: 2, alignSelf: 'center' }} />
                <span style={{ color: 'var(--color-text-muted)' }}>{p.name}:</span>
                <strong>{fmt(p)}</strong>
              </div>
            ))}
            {summary && (
              <div style={{ display: 'flex', gap: 8, color: 'var(--color-text-muted)' }}>
                <span style={{ marginLeft: 16 }}>{summary.label}:</span>
                <strong style={{ color: 'var(--color-text)' }}>{fmtCurrency(summary.value)}</strong>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BreakdownTable({ months, currentMonth }: { months: SalesOverlayMonth[]; currentMonth: string }) {
  // Column sort wiring. Every column ends up either sorting by month
  // (string compare is correct on YYYY-MM) or numerically; the hook
  // handles tie-breaking and asc/desc toggling.
  const { sorted, sort } = useTableSort(
    months,
    {
      month:               (m) => m.month,
      shipped:             (m) => m.shipped,
      wip:                 (m) => m.wip,
      wip_done:            (m) => m.wip_done,
      closed_won:          (m) => m.closed_won,
      forecast:            (m) => m.forecast,
      forecast_unweighted: (m) => m.forecast_unweighted,
      deposit:             (m) => m.invoice_deposit,
      billed:              (m) => m.invoice_billed,
      paid:                (m) => m.invoice_paid,
      employees:           (m) => m.active_employees,
      engagements:         (m) => m.active_engagements,
      engagements_shipped: (m) => m.engagements_shipped,
      deals_closed_won:    (m) => m.deals_closed_won,
      open_engagements:    (m) => m.open_engagements,
      avg_open_age_weeks:  (m) => m.avg_open_age_weeks,
    },
    { key: 'month', dir: 'asc' },
  );
  if (months.length === 0) return <EmptyState message="No data in window." />;
  return (
    <div style={{ maxHeight: 'calc(100vh - 380px)', overflow: 'auto' }}>
      <Table>
        <thead>
          <tr>
            <SortableTh columnKey="month"               sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky>Month</SortableTh>
            <SortableTh columnKey="shipped"             sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Shipped</SortableTh>
            <SortableTh columnKey="wip"                 sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Planned Shipped</SortableTh>
            <SortableTh columnKey="wip_done"            sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Revenue Recognized</SortableTh>
            <SortableTh columnKey="closed_won"          sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Closed Won</SortableTh>
            <SortableTh columnKey="forecast"            sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Forecast (weighted)</SortableTh>
            <SortableTh columnKey="forecast_unweighted" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Pipeline (unweighted)</SortableTh>
            <SortableTh columnKey="deposit"             sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Deposit</SortableTh>
            <SortableTh columnKey="billed"              sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Billed</SortableTh>
            <SortableTh columnKey="paid"                sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Paid</SortableTh>
            <SortableTh columnKey="employees"           sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Active employees</SortableTh>
            <SortableTh columnKey="engagements"         sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Active engagements</SortableTh>
            <SortableTh columnKey="engagements_shipped" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Engagements shipped</SortableTh>
            <SortableTh columnKey="deals_closed_won"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Deals closed won</SortableTh>
            <SortableTh columnKey="open_engagements"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Open engagements</SortableTh>
            <SortableTh columnKey="avg_open_age_weeks"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} sticky right>Avg open age (wks)</SortableTh>
          </tr>
        </thead>
        <tbody>
          {sorted.map((m) => {
            const isCurrent = m.month === currentMonth;
            const isFuture = m.month > currentMonth;
            return (
              <Tr key={m.month}>
                <Td bold style={isCurrent ? { color: 'var(--color-blue)' } : undefined}>
                  {fmtMonth(m.month)}
                  {isCurrent && (
                    <span style={{ marginLeft: 6, fontSize: 9, color: 'var(--color-blue)' }}>now</span>
                  )}
                </Td>
                <Td right style={{ color: m.shipped > 0 ? 'var(--color-green)' : 'var(--color-text-muted)' }}>
                  {m.shipped > 0 ? fmtCurrency(m.shipped) : '—'}
                </Td>
                <Td right style={{ color: m.wip > 0 ? 'var(--color-amber-text)' : 'var(--color-text-muted)' }}>
                  {m.wip > 0 ? fmtCurrency(m.wip) : '—'}
                </Td>
                <Td right style={{ color: m.wip_done > 0 ? 'var(--color-brand-red)' : 'var(--color-text-muted)' }}>
                  {m.wip_done > 0 ? fmtCurrency(m.wip_done) : '—'}
                </Td>
                <Td right style={{ color: m.closed_won > 0 ? 'var(--color-blue)' : 'var(--color-text-muted)' }}>
                  {m.closed_won > 0 ? fmtCurrency(m.closed_won) : '—'}
                </Td>
                <Td right
                    style={{
                      color: m.forecast > 0 ? 'var(--color-text)' : 'var(--color-text-muted)',
                      // De-emphasise past-month forecasts — they're stale by
                      // definition; what closed actually closed.
                      opacity: isFuture || isCurrent ? 1 : 0.5,
                    }}>
                  {m.forecast > 0 ? fmtCurrency(m.forecast) : '—'}
                </Td>
                <Td right muted style={{ fontSize: 11 }}>
                  {m.forecast_unweighted > 0 ? fmtCurrency(m.forecast_unweighted) : '—'}
                </Td>
                <Td right muted style={{ fontSize: 11 }}>
                  {m.invoice_deposit > 0 ? fmtCurrency(m.invoice_deposit) : '—'}
                </Td>
                <Td right style={{ fontSize: 11, color: m.invoice_billed > 0 ? 'var(--color-blue-text)' : 'var(--color-text-muted)' }}>
                  {m.invoice_billed > 0 ? fmtCurrency(m.invoice_billed) : '—'}
                </Td>
                <Td right style={{ fontSize: 11, color: m.invoice_paid > 0 ? 'var(--color-green-text)' : 'var(--color-text-muted)' }}>
                  {m.invoice_paid > 0 ? fmtCurrency(m.invoice_paid) : '—'}
                </Td>
                <Td right style={{ fontSize: 11, color: m.active_employees > 0 ? 'var(--color-text)' : 'var(--color-text-muted)' }}>
                  {m.active_employees > 0 ? m.active_employees : '—'}
                </Td>
                <Td right style={{ fontSize: 11, color: m.active_engagements > 0 ? 'var(--color-text)' : 'var(--color-text-muted)' }}>
                  {m.active_engagements > 0 ? m.active_engagements : '—'}
                </Td>
                <Td right style={{ fontSize: 11, color: m.engagements_shipped > 0 ? 'var(--color-green)' : 'var(--color-text-muted)' }}>
                  {m.engagements_shipped > 0 ? m.engagements_shipped : '—'}
                </Td>
                <Td right style={{ fontSize: 11, color: m.deals_closed_won > 0 ? 'var(--color-blue)' : 'var(--color-text-muted)' }}>
                  {m.deals_closed_won > 0 ? m.deals_closed_won : '—'}
                </Td>
                <Td right style={{ fontSize: 11, color: m.open_engagements > 0 ? 'var(--color-amber-text)' : 'var(--color-text-muted)' }}>
                  {m.open_engagements > 0 ? m.open_engagements : '—'}
                </Td>
                <Td right style={{ fontSize: 11, color: m.avg_open_age_weeks > 0 ? 'var(--color-chart-purple)' : 'var(--color-text-muted)' }}>
                  {m.avg_open_age_weeks > 0 ? m.avg_open_age_weeks : '—'}
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

/** CSV export — same column set + ordering as the on-screen table.
 *  Empty cells are represented as 0 in the CSV so finance can sum cleanly
 *  without dash-handling logic. */
function downloadOverlayCsv(months: SalesOverlayMonth[]) {
  const rows: Array<Array<unknown>> = [[
    'Month', 'Shipped', 'Planned Shipped', 'Revenue Recognized',
    'Closed Won', 'Forecast (weighted)', 'Pipeline (unweighted)',
    'Deposit', 'Billed', 'Paid',
    'Active employees', 'Active engagements', 'Engagements shipped', 'Deals closed won', 'Open engagements',
    'Avg open age (wks)',
  ]];
  for (const m of months) {
    rows.push([
      m.month,
      m.shipped.toFixed(2),
      m.wip.toFixed(2),
      m.wip_done.toFixed(2),
      m.closed_won.toFixed(2),
      m.forecast.toFixed(2),
      m.forecast_unweighted.toFixed(2),
      m.invoice_deposit.toFixed(2),
      m.invoice_billed.toFixed(2),
      m.invoice_paid.toFixed(2),
      m.active_employees,
      m.active_engagements,
      m.engagements_shipped,
      m.deals_closed_won,
      m.open_engagements,
      m.avg_open_age_weeks,
    ]);
  }
  downloadCsv('sales-overlay.csv', rows);
}

/** YYYY-MM → "Mar '26" short form for the chart axis. Keeps the X-axis
 *  readable when the chart compresses 25 months into a single row. */
function shortMonthLabel(yyyyMm: string): string {
  const [y, m] = yyyyMm.split('-').map((s) => parseInt(s, 10));
  if (!y || !m) return yyyyMm;
  const d = new Date(y, m - 1, 1);
  return `${d.toLocaleString('en-US', { month: 'short' })} '${String(y).slice(-2)}`;
}

// Unused fallback to satisfy the linter if SalesOverlayReport is referenced
// only via the api.salesOverlay() return type. Keep the import warning quiet.
export type { SalesOverlayReport };
